import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  AlertCircle,
  AlertTriangle,
  Bell,
  Check,
  CheckCheck,
  ExternalLink,
  Info,
  RefreshCw,
  type LucideIcon,
} from 'lucide-react';

import { pageStyles } from '@/components/utils/PageLayout';
import { PageFrame, PageHeader, Surface, Button, Note, Placeholder, TabBar, FilterSelect, Pill, Tag } from '@/components/ui';
import ErrorDisplay from '@/components/utils/ErrorDisplay';
import { notificationsApi } from '@/services/api';
import { useAuth } from '@/contexts/AuthContext';
import { useNotification } from '@/contexts/NotificationContext';
import { useOnPageActive } from '@/hooks/useOnPageActive';
import { formatDateTime, formatRelativeTime } from '@/utils/formatters';
import styles from './Notifications.module.css';

type NotificationType = 'error' | 'warning' | 'info' | null;

interface NotificationListItem {
  id: number;
  notification_id: number;
  title: string;
  message: string;
  notification_type: NotificationType;
  module?: string | null;
  is_actionable: boolean;
  action_url?: string | null;
  is_read: boolean;
  is_completed: boolean;
  created_ts: string;
  last_reminded_at?: string | null;
}

interface TabCounts {
  all_count: number;
  actionable_count: number;
  error_count: number;
  warning_count: number;
  info_count: number;
}

interface PaginationState {
  next_cursor: string | null;
  has_more: boolean;
}

/** Shape of `notificationsApi.listNotifications` — the API object types it as `any`. */
interface ListNotificationsResponse {
  results?: NotificationListItem[];
  pagination?: { next_cursor?: string | null; has_more?: boolean };
  all_count?: number;
  actionable_count?: number;
  error_count?: number;
  warning_count?: number;
  info_count?: number;
}

interface ListNotificationsParams {
  user_id: number;
  page_size?: number;
  type?: string;
  cursor?: string | null;
  days?: number;
  start_date?: string;
  end_date?: string;
}

interface DateFilterState {
  days: number;
  startDate: string;
  endDate: string;
  useCustomRange: boolean;
}

const PAGE_SIZE = 20;

const DEFAULT_DAYS = 2;

const EMPTY_COUNTS: TabCounts = {
  all_count: 0,
  actionable_count: 0,
  error_count: 0,
  warning_count: 0,
  info_count: 0,
};

const TABS: { value: string; label: string; countKey: keyof TabCounts; countClass: string }[] = [
  { value: 'all', label: 'All', countKey: 'all_count', countClass: 'count-primary' },
  { value: 'actionable', label: 'Actionable', countKey: 'actionable_count', countClass: 'count-danger' },
  { value: 'error', label: 'Errors', countKey: 'error_count', countClass: 'count-danger' },
  { value: 'warning', label: 'Warnings', countKey: 'warning_count', countClass: 'count-warning' },
  { value: 'info', label: 'Info', countKey: 'info_count', countClass: 'count-info' },
];

const DATE_RANGE_OPTIONS = [
  { value: '1', label: 'Last 24 hours' },
  { value: '2', label: 'Last 2 days' },
  { value: '7', label: 'Last 7 days' },
  { value: '30', label: 'Last 30 days' },
  { value: '90', label: 'Last 90 days' },
];

/** The presets plus the sentinel that reveals the From/To date inputs. */
const RANGE_SELECT_OPTIONS = [...DATE_RANGE_OPTIONS, { value: 'custom', label: 'Custom range' }];

/** Severity is carried by the row's icon alone — the row itself stays neutral. */
interface TypeMeta {
  icon: LucideIcon;
  iconClass: string;
}

const TYPE_META: Record<'error' | 'warning' | 'info', TypeMeta> = {
  error: { icon: AlertCircle, iconClass: styles['icon-error'] },
  warning: { icon: AlertTriangle, iconClass: styles['icon-warning'] },
  info: { icon: Info, iconClass: styles['icon-info'] },
};

const NEUTRAL_TYPE_META: TypeMeta = { icon: Bell, iconClass: '' };

const typeMetaFor = (type: NotificationType): TypeMeta =>
  (type && TYPE_META[type]) || NEUTRAL_TYPE_META;

/**
 * Notification bodies are authored server-side and may carry links, lists and
 * small tables, so they are rendered as HTML. Escaping happens on the backend —
 * do not accept operator-authored HTML through this path.
 */
const asHTML = (htmlString: string) => ({ __html: htmlString });

const WEEK_MS = 7 * 24 * 60 * 60 * 1000;

/**
 * Relative time reads best while an item is fresh; past a week "63d ago" says
 * less than the stamp itself, so the two shared formatters swap over.
 */
const timeLabel = (iso: string): string =>
  Date.now() - new Date(iso).getTime() >= WEEK_MS ? formatDateTime(iso) : formatRelativeTime(iso);

const dedupeById = (items: NotificationListItem[]): NotificationListItem[] => {
  const map = new Map<number, NotificationListItem>();
  for (const item of items) {
    if (!map.has(item.id)) map.set(item.id, item);
  }
  return Array.from(map.values());
};

// ---- Row -------------------------------------------------------------------

interface NotificationRowProps {
  notification: NotificationListItem;
  busy: boolean;
  onMarkAsRead: (id: number) => void;
  onMarkAsCompleted: (notificationId: number) => void;
}

const NotificationRow: React.FC<NotificationRowProps> = React.memo(({
  notification, busy, onMarkAsRead, onMarkAsCompleted,
}: NotificationRowProps) => {
  const meta = typeMetaFor(notification.notification_type);
  const Icon = meta.icon;
  const showReminder =
    notification.is_actionable && !!notification.last_reminded_at && !notification.is_completed;

  const itemClasses = [
    styles.item,
    notification.is_read ? '' : styles['item-unread'],
    notification.is_completed ? styles['item-completed'] : '',
  ].filter(Boolean).join(' ');

  return (
    <article className={itemClasses}>
      <span className={`${styles.icon} ${meta.iconClass}`}>
        <Icon size={16} />
      </span>

      <div className={styles.body}>
        <div className={styles['title-row']}>
          {!notification.is_read && (
            <span className={styles['unread-dot']} title="Unread" aria-label="Unread" />
          )}
          <span className={styles.title}>{notification.title || 'Notification'}</span>
          {/* The module is a classification (Tag); the rest are states (Pill). */}
          {notification.module && <Tag>{notification.module}</Tag>}
          {notification.is_actionable && !notification.is_completed && (
            <Pill tone="warning">Action required</Pill>
          )}
          {notification.is_actionable && notification.is_completed && (
            <Pill tone="positive">Completed</Pill>
          )}
          {showReminder && <Pill tone="info">Reminder</Pill>}
        </div>

        <div className={styles.message} dangerouslySetInnerHTML={asHTML(notification.message)} />

        <div className={styles.meta}>
          <span title={formatDateTime(notification.created_ts)}>
            {timeLabel(notification.created_ts)}
          </span>
          {showReminder && notification.last_reminded_at && (
            <>
              <span className={styles['meta-sep']}>•</span>
              <span
                className={styles['meta-reminder']}
                title={formatDateTime(notification.last_reminded_at)}
              >
                Reminded {timeLabel(notification.last_reminded_at)}
              </span>
            </>
          )}
        </div>
      </div>

      <div className={styles.actions}>
        {/* A real navigation target, so it stays an anchor rather than a Button —
            middle-click and "open in new tab" have to keep working. */}
        {notification.action_url && (
          <a
            className={styles['link-action']}
            href={notification.action_url}
            target="_blank"
            rel="noopener noreferrer"
            title="Open linked page"
            aria-label={`Open linked page for ${notification.title}`}
          >
            <ExternalLink size={13} />
          </a>
        )}

        {notification.is_actionable && !notification.is_completed && (
          <Button
            size="sm"
            onClick={() => onMarkAsCompleted(notification.notification_id)}
            disabled={busy}
            title="Mark this action as completed"
            icon={<Check size={13} />}
          >
            Complete
          </Button>
        )}

        {!notification.is_read && (
          <Button
            size="sm"
            onClick={() => onMarkAsRead(notification.id)}
            disabled={busy}
            title="Mark as read"
            icon={<CheckCheck size={13} />}
          >
            Read
          </Button>
        )}
      </div>
    </article>
  );
});
NotificationRow.displayName = 'NotificationRow';

// ---- Page ------------------------------------------------------------------

const deepLinkDays = (params: URLSearchParams): number | null => {
  const days = Number(params.get('days'));
  return Number.isFinite(days) && days > 0 ? days : null;
};

const deepLinkTab = (params: URLSearchParams): string | null => {
  const tab = params.get('tab');
  return tab && TABS.some(t => t.value === tab) ? tab : null;
};

const AllNotifications: React.FC = () => {
  const { user } = useAuth();
  const { refreshNotifications } = useNotification();
  const [searchParams] = useSearchParams();

  const [notifications, setNotifications] = useState<NotificationListItem[]>([]);
  const [tabCounts, setTabCounts] = useState<TabCounts>(EMPTY_COUNTS);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // `id` and `notification_id` are different ID spaces, so busy state is
  // tracked as an (id, action) pair rather than a bare id — otherwise a
  // coincidental match across spaces would mark an unrelated row busy too.
  const [busyAction, setBusyAction] = useState<{ id: number; kind: 'read' | 'completed' } | null>(null);
  const [hasNewer, setHasNewer] = useState(false);

  // Seeded from the deep link (?days=, ?tab=) up front so the first fetch
  // already uses them, instead of fetching the defaults and then refetching.
  const [currentTab, setCurrentTab] = useState(() => deepLinkTab(searchParams) ?? 'all');
  const [dateFilter, setDateFilter] = useState<DateFilterState>(() => ({
    days: deepLinkDays(searchParams) ?? DEFAULT_DAYS,
    startDate: '',
    endDate: '',
    useCustomRange: false,
  }));

  const [pagination, setPagination] = useState<PaginationState>({
    next_cursor: null,
    has_more: false,
  });
  // Bumped to re-run the load effect without touching the filters.
  const [reloadKey, setReloadKey] = useState(0);

  const abortRef = useRef<AbortController | null>(null);
  // Lets the polling effect compare against the newest loaded item without
  // depending on `notifications` — otherwise the interval resets on every fetch.
  const latestIdRef = useRef<number | null>(null);
  const loadingRef = useRef(loading);
  const loadingMoreRef = useRef(loadingMore);

  useEffect(() => {
    latestIdRef.current = notifications[0]?.id ?? null;
  }, [notifications]);

  useEffect(() => {
    loadingRef.current = loading;
  }, [loading]);

  useEffect(() => {
    loadingMoreRef.current = loadingMore;
  }, [loadingMore]);

  // Deep links from the header bell carry ?days= and ?tab=. Keyed on the
  // location key, not the params, so "View all" reloads the list even when it
  // links to the URL already open. Skipped on mount: the initial state already
  // applied the link, and the load effect makes the first fetch.
  const location = useLocation();
  const mountKeyRef = useRef(location.key);
  useEffect(() => {
    if (location.key === mountKeyRef.current) return;
    const days = deepLinkDays(searchParams);
    if (days !== null) {
      setDateFilter(prev => ({ ...prev, days, useCustomRange: false, startDate: '', endDate: '' }));
    }
    const tab = deepLinkTab(searchParams);
    if (tab) setCurrentTab(tab);
    setReloadKey(key => key + 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  const buildParams = useCallback((): ListNotificationsParams | null => {
    if (!user?.id) return null;
    // Switching to "custom" leaves both dates empty until the user fills them
    // in — skip fetching until the range is actually usable, instead of
    // firing a fallback-to-days call for every keystroke along the way.
    if (dateFilter.useCustomRange && (!dateFilter.startDate || !dateFilter.endDate)) return null;
    const params: ListNotificationsParams = {
      user_id: user.id,
      page_size: PAGE_SIZE,
      type: currentTab,
    };
    if (dateFilter.useCustomRange) {
      params.start_date = dateFilter.startDate;
      params.end_date = dateFilter.endDate;
    } else {
      params.days = dateFilter.days;
    }
    return params;
  }, [user?.id, currentTab, dateFilter]);

  const applyResponse = useCallback((response: ListNotificationsResponse, reset: boolean) => {
    const results: NotificationListItem[] = response.results || [];
    if (reset) {
      setTabCounts({
        all_count: response.all_count || 0,
        actionable_count: response.actionable_count || 0,
        error_count: response.error_count || 0,
        warning_count: response.warning_count || 0,
        info_count: response.info_count || 0,
      });
    }
    setPagination({
      next_cursor: response.pagination?.next_cursor || null,
      has_more: response.pagination?.has_more || false,
    });
    setNotifications(prev => (reset ? dedupeById(results) : dedupeById([...prev, ...results])));
  }, []);

  const isCancelled = (err: unknown): boolean => {
    const e = err as { code?: string; name?: string };
    return e?.code === 'ERR_CANCELED' || e?.name === 'CanceledError' || e?.name === 'AbortError';
  };

  // Reload from the first page whenever the tab or the date range changes.
  useEffect(() => {
    const params = buildParams();
    if (!params) return;

    let cancelled = false;
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setLoading(true);
    setError(null);
    setHasNewer(false);

    notificationsApi.listNotifications(params, { signal: controller.signal })
      .then(response => {
        if (cancelled) return;
        applyResponse(response, true);
      })
      .catch(err => {
        if (cancelled || isCancelled(err)) return;
        console.error('Error fetching notifications:', err);
        setNotifications([]);
        setPagination({ next_cursor: null, has_more: false });
        setError('Failed to load notifications.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [buildParams, applyResponse, reloadKey]);

  const reload = useCallback(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setReloadKey(key => key + 1);
  }, []);

  const loadMore = useCallback(async () => {
    const params = buildParams();
    if (!params || loadingRef.current || loadingMoreRef.current || !pagination.has_more || !pagination.next_cursor) return;

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setLoadingMore(true);
    try {
      const response = await notificationsApi.listNotifications(
        { ...params, cursor: pagination.next_cursor },
        { signal: controller.signal }
      );
      applyResponse(response, false);
    } catch (err) {
      if (!isCancelled(err)) {
        console.error('Error loading more notifications:', err);
        toast.error('Failed to load more notifications.');
      }
    } finally {
      setLoadingMore(false);
    }
  }, [buildParams, applyResponse, pagination]);

  // Infinite scroll — the Load more button stays as the explicit fallback.
  useEffect(() => {
    if (!pagination.has_more) return;

    let timeoutId: number | undefined;
    const handleScroll = () => {
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = window.setTimeout(() => {
        if (window.innerHeight + window.scrollY >= document.body.offsetHeight - 200) loadMore();
      }, 100);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [pagination.has_more, loadMore]);

  // When the user comes back to the page, check for anything newer than the top of the
  // list and offer a reload. No polling: an idle tab makes no requests.
  const buildParamsRef = useRef(buildParams);
  buildParamsRef.current = buildParams;
  const checkForNewer = useCallback(async () => {
    const requestedWith = buildParamsRef.current;
    const params = requestedWith();
    if (!params) return;
    try {
      const response = await notificationsApi.listNotifications({ ...params, page_size: 1 });
      const latest: NotificationListItem | undefined = response.results?.[0];
      // The filters changed while this was in flight, so it describes the old list.
      if (buildParamsRef.current !== requestedWith || !latest) return;
      if (latestIdRef.current !== null && latest.id !== latestIdRef.current) setHasNewer(true);
    } catch (err) {
      console.error('Notification check failed:', err);
    }
  }, []);
  useOnPageActive(checkForNewer);

  const handleMarkAsRead = useCallback(async (id: number) => {
    if (!user?.id) return;
    setBusyAction({ id, kind: 'read' });
    try {
      await notificationsApi.markAsRead(id, user.id);
      setNotifications(prev => prev.map(n => (n.id === id ? { ...n, is_read: true } : n)));
      refreshNotifications();
    } catch {
      toast.error('Failed to mark as read.');
    } finally {
      setBusyAction(null);
    }
  }, [user?.id, refreshNotifications]);

  const handleMarkAllAsRead = useCallback(async () => {
    if (!user?.id) return;
    try {
      const response = await notificationsApi.markAllAsRead(user.id);
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      toast.success(
        response?.updated_count
          ? `${response.updated_count} notification${response.updated_count === 1 ? '' : 's'} marked as read.`
          : 'All notifications marked as read.'
      );
      refreshNotifications();
    } catch {
      toast.error('Failed to mark all as read.');
    }
  }, [user?.id, refreshNotifications]);

  const handleMarkAsCompleted = useCallback(async (notificationId: number) => {
    if (!user?.id) return;
    setBusyAction({ id: notificationId, kind: 'completed' });
    try {
      await notificationsApi.markAsCompleted(notificationId, user.id);
      setNotifications(prev =>
        prev.map(n => (n.notification_id === notificationId ? { ...n, is_completed: true } : n))
      );
      toast.success('Marked as completed.');
      refreshNotifications();
    } catch (err) {
      const message =
        (err as { response?: { data?: { error?: string } } })?.response?.data?.error
        || 'Failed to mark as completed.';
      toast.error(message);
    } finally {
      setBusyAction(null);
    }
  }, [user?.id, refreshNotifications]);

  const handleDateRangeChange = useCallback((value: string) => {
    if (value === 'custom') {
      setDateFilter(prev => ({ ...prev, useCustomRange: true }));
    } else {
      setDateFilter({
        days: Number(value),
        startDate: '',
        endDate: '',
        useCustomRange: false,
      });
    }
  }, []);

  const hasActiveFilters =
    currentTab !== 'all' || dateFilter.useCustomRange || dateFilter.days !== DEFAULT_DAYS;

  const handleClearFilters = useCallback(() => {
    setCurrentTab('all');
    setDateFilter({ days: DEFAULT_DAYS, startDate: '', endDate: '', useCustomRange: false });
  }, []);

  const unreadCount = useMemo(
    () => notifications.filter(n => !n.is_read).length,
    [notifications]
  );

  const rows = useMemo(() => notifications.map(notification => (
    <NotificationRow
      key={notification.id}
      notification={notification}
      busy={
        (busyAction?.kind === 'read' && busyAction.id === notification.id)
        || (busyAction?.kind === 'completed' && busyAction.id === notification.notification_id)
      }
      onMarkAsRead={handleMarkAsRead}
      onMarkAsCompleted={handleMarkAsCompleted}
    />
  )), [notifications, busyAction, handleMarkAsRead, handleMarkAsCompleted]);

  const emptyTitle = currentTab === 'all'
    ? 'No notifications'
    : `No ${TABS.find(t => t.value === currentTab)?.label.toLowerCase() ?? ''} notifications`;

  return (
    <PageFrame
      header={
        <PageHeader
          title="Notifications"
          subtitle="Everything raised for you, newest first."
          actions={
            <Button onClick={handleMarkAllAsRead} disabled={loading} icon={<CheckCheck size={15} />}>
              Mark all as read
            </Button>
          }
        />
      }
    >
      {/* Tabs and the date range share one row — the range is the page's only
          filter, so a full-width filter card underneath was mostly empty. */}
      <div className={styles['tab-row']}>
        <TabBar
          ariaLabel="Notification type"
          value={currentTab}
          onChange={setCurrentTab}
          tabs={TABS.map(tab => ({
            value: tab.value,
            label: tab.label,
            badge: (
              <span
                className={`${styles['tab-count']} ${currentTab === tab.value ? styles[tab.countClass] : ''}`}
              >
                {tabCounts[tab.countKey]}
              </span>
            ),
          }))}
        />

        <div className={styles['tab-row-filters']}>
          <div className={styles['range-select']}>
            <FilterSelect
              label="Date Range"
              options={RANGE_SELECT_OPTIONS}
              value={
                RANGE_SELECT_OPTIONS.find(
                  o => o.value === (dateFilter.useCustomRange ? 'custom' : String(dateFilter.days)),
                ) ?? null
              }
              onChange={opt => handleDateRangeChange(opt?.value ?? String(DEFAULT_DAYS))}
            />
          </div>

          {dateFilter.useCustomRange && (
            <>
              <div className={pageStyles['filter-group']}>
                <label htmlFor="notification-start-date">From</label>
                <input
                  id="notification-start-date"
                  type="date"
                  className={pageStyles.input}
                  value={dateFilter.startDate}
                  max={dateFilter.endDate || undefined}
                  onChange={e => setDateFilter(prev => ({ ...prev, startDate: e.target.value }))}
                />
              </div>
              <div className={pageStyles['filter-group']}>
                <label htmlFor="notification-end-date">To</label>
                <input
                  id="notification-end-date"
                  type="date"
                  className={pageStyles.input}
                  value={dateFilter.endDate}
                  min={dateFilter.startDate || undefined}
                  onChange={e => setDateFilter(prev => ({ ...prev, endDate: e.target.value }))}
                />
              </div>
            </>
          )}

          {hasActiveFilters && (
            <Button onClick={handleClearFilters}>Clear filters</Button>
          )}
        </div>
      </div>

      {hasNewer && (
        <Note tone="info">
          <span className={styles['new-banner']}>
            New notifications have arrived.
            <Button size="sm" onClick={reload} icon={<RefreshCw size={13} />}>Reload</Button>
          </span>
        </Note>
      )}

      {error && (
        <ErrorDisplay
          title="Could not load notifications"
          message={error}
          onRetry={reload}
        />
      )}

      {loading ? (
        <Surface>
          <Placeholder kind="loading" message="Loading notifications…" />
        </Surface>
      ) : notifications.length === 0 ? (
        !error && (
          <Surface>
            <Placeholder
              message={emptyTitle}
              hint="Nothing in the selected date range. Try widening it from the Date Range filter."
            />
          </Surface>
        )
      ) : (
        <Surface footer={
          <div className={styles['list-footer']}>
            <span className={pageStyles['count-info']}>
              {notifications.length} loaded
              {unreadCount > 0 && ` · ${unreadCount} unread`}
            </span>
            <span className={styles['list-footer-action']}>
              {loadingMore && <span className={pageStyles.spinner} />}
              {!loadingMore && pagination.has_more && (
                <Button onClick={loadMore}>Load more</Button>
              )}
              {!loadingMore && !pagination.has_more && (
                <span className={styles['end-note']}>End of notifications</span>
              )}
            </span>
          </div>
        }>
          <div className={styles.list}>{rows}</div>
        </Surface>
      )}
    </PageFrame>
  );
};

export default AllNotifications;
